import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, Dumbbell, HeartHandshake, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { dashboardFor } from "@/components/ProtectedRoute";
import { CountryPhoneBlock } from "@/components/CountryPhone";

const ROLES = [
  {
    role: "member",
    title: "Register as a Member",
    tagline: "I want an accountability partner to help me stay consistent.",
    Icon: Dumbbell,
    testid: "role-select-member-card",
  },
  {
    role: "partner",
    title: "Register as an Accountability Partner",
    tagline: "I want to motivate, support and earn rewards.",
    Icon: HeartHandshake,
    testid: "role-select-partner-card",
  },
];

export default function GetStarted() {
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [country, setCountry] = useState(null);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate(dashboardFor(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!country) {
      setError("Please select your country.");
      return;
    }
    if (!phone.trim()) {
      setError("Please enter your phone / WhatsApp number.");
      return;
    }
    setLoading(true);
    const res = await register({
      ...form,
      role,
      country: country.name,
      country_code: country.code,
      dial_code: country.dial,
      phone: phone.trim(),
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Account created! Welcome to FitLynk.");
      navigate(dashboardFor(res.user.role), { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] fl-grid-bg" data-testid="get-started-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-8"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
        <Logo size="h-9" className="mb-10" />

        {!role ? (
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">Get Started</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">How do you want to join?</h1>
            <p className="mt-3 text-zinc-400 max-w-xl">Choose your path. You'll use the same login either way — FitLynk shows you the right experience based on your role.</p>
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {ROLES.map(({ role: r, title, tagline, Icon, testid }) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  data-testid={testid}
                  className="group text-left rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 hover:border-[#E53E3E] hover:-translate-y-1 transition-all"
                >
                  <span className="grid place-items-center w-14 h-14 rounded-xl bg-[#E53E3E]/10 text-[#E53E3E] group-hover:bg-[#E53E3E] group-hover:text-white transition-colors"><Icon className="w-7 h-7" /></span>
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight">{title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{tagline}</p>
                  <span className="mt-5 inline-flex items-center text-sm font-semibold text-[#E53E3E]">Continue <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
                </button>
              ))}
            </div>
            <p className="mt-8 text-sm text-zinc-400">Already have an account? <Link to="/login" className="text-[#E53E3E] font-semibold hover:underline">Log in</Link></p>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <button onClick={() => { setRole(null); setError(""); }} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6" data-testid="register-back-button"><ArrowLeft className="w-4 h-4" /> Choose a different role</button>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#E53E3E] bg-[#E53E3E]/10 px-3 py-1 rounded-full">
              <Check className="w-3 h-3" /> {role === "member" ? "Member" : "Accountability Partner"}
            </span>
            <h1 className="mt-4 font-display text-3xl font-black uppercase tracking-tight">Create your account</h1>
            {role === "partner" && <p className="mt-2 text-sm text-zinc-400">Partner accounts are reviewed by our team before activation.</p>}

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-300">Full name</Label>
                <Input id="name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Carter" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="register-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="register-email-input" />
              </div>
              <CountryPhoneBlock country={country} onCountry={setCountry} phone={phone} onPhone={setPhone} idPrefix="register" />
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="register-password-input" />
              </div>
              {error && <p className="text-sm text-[#E53E3E]" data-testid="register-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold" data-testid="register-form-submit-button">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-zinc-400 text-center">Already have an account? <Link to="/login" className="text-[#E53E3E] font-semibold hover:underline">Log in</Link></p>
          </div>
        )}
      </div>
    </div>
  );
}
